import type { CSSProperties } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { bezierCurve } from 'styles/tools/constants'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import GoogleLongLogo from 'components/Common/TrustIndicators/GoogleLongLogo'
import TrustPilotLogoCombined from 'components/Account/AccountAccess/TrustPilotLogoCombined'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCarouselAutoPlay from 'hooks/useCarouselAutoPlay'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { useScreenSizeUp } from 'hooks/useScreenSize'

const SLIDE_COUNT = 2
const SLIDE_DURATION_MS = 3000

const USPs = styled.div`
  display: grid;
  overflow: visible;
  grid-template-columns: repeat(${SLIDE_COUNT}, 100%);
  width: 100%;
  transition: transform 0.5s ${bezierCurve};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(2, auto);
    grid-gap: ${rem(16)};
    grid-template-areas:
      "google trustpilot";

    .trustpilot-logo {
      grid-area: trustpilot;
    }
    .google-logo {
      grid-area: google;
    }
  }
`

const AnimationContainer = styled.div`
  overflow: hidden;
`

export function TrustIndicatorSingapore() {
  const intl = useIntl()

  const isCarouselEnabled = !useScreenSizeUp('tablet')
  const { activeIndex, goToSlide, paused, setPaused } = useCarouselAutoPlay(SLIDE_COUNT, SLIDE_DURATION_MS, { enabled: isCarouselEnabled })

  const slideStyle: CSSProperties | undefined = isCarouselEnabled ? {
    transform: `translateX(${-activeIndex * 100}%)`,
  } : undefined

  return (
    <AnimationContainer>
      <USPs style={slideStyle}>
        <Group className="google-logo" direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
          <GoogleLongLogo width="54" height="18" />
          <BodyTextBlock colour="light-foreground-alt" variant="medium" weight="bold">
            {intl.formatMessage({
              id: 'NxdXpumsvI6BCE0yXas8',
              defaultMessage: '4.7/5',
              description: 'Google rating score in trust indicator on the homepage',
            })}
          </BodyTextBlock>
        </Group>
        <Group className="trustpilot-logo" direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
          <TrustPilotLogoCombined />
          <BodyTextBlock colour="light-foreground-alt" weight="bold" variant="medium" wrap="no-wrap">
            {intl.formatMessage({
              id: 'rgp5rPUfA8Ke2p3zj3Rm',
              defaultMessage: '4.8/5',
              description: 'Trustpilot rating score in trust indicator on the homepage',
            })}
          </BodyTextBlock>
        </Group>
      </USPs>
      <CSSBreakpoint max="mobile">
        <PaginationDots
          count={SLIDE_COUNT}
          activeIndex={activeIndex}
          slideDuration={SLIDE_DURATION_MS}
          onSelect={goToSlide}
          paused={paused}
          onHoverChange={setPaused}
        />
      </CSSBreakpoint>
    </AnimationContainer>
  )
}
